import {ApolloProvider} from '@apollo/client';
import {useMemo} from 'react';
import {createApolloClient} from 'src/internal/apollo/client';
import DataConfigContext from 'src/internal/config/DataConfigContext';
import useOnChangedUser from 'src/internal/user/useOnChangedUser';
import addLanguageHeaders from 'src/public/headers/addLanguageHeaders';
import Headers from 'src/public/headers/headers';

import type {ReactElement, ReactNode} from 'react';
import type {DataConfig} from 'src/internal/config/types';
import type {FetchOptions, FetchUrl} from 'src/internal/fetch/types';

type Props = {
  children: ReactNode;
  config: DataConfig;
};

function OnChangeLoggedUser({
  onChangeLoggedUser,
}: {
  onChangeLoggedUser: () => void;
}) {
  useOnChangedUser(onChangeLoggedUser);
  return null;
}

export default function DataProvider({children, config}: Props): ReactElement {
  const client = useMemo(
    () => (config.graphql ? createApolloClient(config.graphql) : null),
    [config.graphql],
  );

  // The `<DataProvider>` ensures that when calling the provided fetch, headers
  // are always passed as a record following the `FetchOptions` type. The
  // normalization is made by the `@sail/data` `Headers` class.
  const fetch = useMemo(() => {
    const fetch = config.fetch;

    if (!fetch) {
      return fetch;
    }

    return (url: FetchUrl, options?: FetchOptions) => {
      const headers = new Headers(options?.headers ?? {});

      if (config.locale) {
        addLanguageHeaders(headers, config.locale);
      }

      return fetch(url, {
        ...options,
        headers: headers.getFetchHeaders(),
      });
    };
  }, [config.fetch, config.locale]);

  const configWithFetch = useMemo(() => {
    return {
      ...config,
      fetch,
    };
  }, [config, fetch]);

  if (!client) {
    return (
      <DataConfigContext.Provider value={configWithFetch}>
        {children}
      </DataConfigContext.Provider>
    );
  }

  const handleChangedUser = config.onChangeLoggedUser;

  return (
    <DataConfigContext.Provider value={configWithFetch}>
      <ApolloProvider client={client}>{children}</ApolloProvider>

      {handleChangedUser ? (
        <OnChangeLoggedUser onChangeLoggedUser={handleChangedUser} />
      ) : (
        <></>
      )}
    </DataConfigContext.Provider>
  );
}
